import { Link } from "react-router-dom";
import styles from "./layout.module.css";
import { Link as MuiLink } from "@mui/material";
import { TuneOutlined, CalendarTodayOutlined, SendOutlined, LocalShippingOutlined, PeopleOutlineOutlined, PersonOutlineOutlined, SettingsOutlined, InventoryOutlined, AssessmentOutlined, Close, MyLocationOutlined, Lock, DomainOutlined, ReceiptLongOutlined } from "@mui/icons-material";
import { authData } from "./GetAuth";
import { useEffect, useRef } from "react";
import { useState } from "react";
import { useWebsite } from "../../context/WebsiteContext";
import { hasFeature, useBillingUpdates } from "./Features";
import axios from "axios";

// Desktop shows submenus as flyouts; below this width they are inline accordions
const isDesktop = () => window.matchMedia('(min-width: 992px)').matches;

// Small lock on a rail icon whose add-on is off; the page itself explains how to enable it
const LockBadge = () => <span className={`${styles.RailLockBadge}`} title="Add-on not enabled"><Lock /></span>;

const Footer = ({ menuName, mobileOpen, onClose }) => {
  useBillingUpdates();
  const auth = authData();
  const isSuper = auth?.user_type === 1;
  const isCompanyAdmin = auth?.user_type === 2;
  const can = (perm) => auth?.permissions?.indexOf(perm) > -1;
  const [openMenus, setOpenMenus] = useState([]);
  const [dropUp, setDropUp] = useState(null);
  const [overdueCount, setOverdueCount] = useState(0);
  const footerRef = useRef(null);

  // Overdue invoice count for the platform Billing badge
  useEffect(() => {
    if (!isSuper || !auth?.api_token) return;
    axios.get(process.env.REACT_APP_API_URL + "v1/admin/invoices", {
      params: { status: "overdue", per_page: 1 },
      headers: { Authorization: `Bearer ${auth.api_token}` },
    }).then((response) => {
      if (response.data.status === "SUCCESS") {
        setOverdueCount(response.data.data?.total || 0);
      }
    }).catch(() => { });
  }, [isSuper, auth?.api_token]);

  // Close the desktop flyout when clicking anywhere outside the sidebar
  useEffect(() => {
    const handleOutside = (e) => {
      if (isDesktop() && footerRef.current && !footerRef.current.contains(e.target)) {
        setOpenMenus([]);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  // Open the flyout upward when there is not enough room below the menu item
  const placeMenu = (name, li) => {
    const list = li && li.querySelector('ul');
    if (!list) return;
    const rect = li.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.top;
    const spaceAbove = rect.bottom;
    setDropUp(list.offsetHeight > spaceBelow - 8 && spaceAbove > spaceBelow ? name : null);
  };

  const openFlyout = (name, li) => {
    setOpenMenus([name]);
    placeMenu(name, li);
  };

  const menuProps = (name) => ({
    onMouseEnter: (e) => isDesktop() && openFlyout(name, e.currentTarget),
    onMouseLeave: () => isDesktop() && setOpenMenus([]),
  });

  const toggleMenu = (name, e) => {
    if (isDesktop()) {
      openFlyout(name, e.currentTarget.closest('li'));
      return;
    }
    setOpenMenus((prev) => (
      prev.includes(name) ? prev.filter((m) => m !== name) : [...prev, name]
    ));
  };

  const closeAll = () => {
    setOpenMenus([]);
    onClose && onClose();
  };

  const dropClass = (name, activeNames = [name]) => `${styles.DropDownSec} ${activeNames.includes(menuName) ? styles.Active : ''} ${openMenus.includes(name) ? styles.Open : ''} ${dropUp === name ? styles.DropUp : ''}`;

  const { websiteData } = useWebsite();

  const shippingOn = hasFeature('shipping_delivery', auth);
  const liveTrackingOn = hasFeature('live_tracking', auth);
  const membershipOn = hasFeature('membership', auth);

  return (<>
    <div
      className={`${styles.SidebarOverlay} ${mobileOpen ? styles.MobileOpen : ''}`}
      onClick={onClose}
      aria-hidden="true"
    />
    <footer ref={footerRef} className={`${styles.FooterMain} ${mobileOpen ? styles.MobileOpen : ''}`}>
      <div className={`${styles.SideAvatarMain}`}>
        <Link to={isSuper ? "/super-admin/dashboard" : "/dashboard"} onClick={closeAll}><img src={websiteData && websiteData?.home_config && websiteData?.home_config?.logoURL ? websiteData?.home_config?.logoURL : "/favicon.png"} alt="" style={{ maxWidth: '65px' }} /></Link>
        <button type="button" className={`${styles.SideCloseBU}`} onClick={onClose} aria-label="Close navigation">
          <Close style={{ color: '#85859e', width: 18, height: 18 }} />
        </button>
      </div>

      <div className={`${styles.SideMenuLinkSec}`}>
        <div className={`${styles.SideMenuLinkSecUnder}`}>
          <ul>
            {/* Platform (super admin) */}
            {isSuper && <li {...menuProps('platform')} className={dropClass('platform', ['sa-dashboard', 'sa-companies', 'sa-activity'])}>
              <MuiLink onClick={(e) => toggleMenu('platform', e)}>
                <DomainOutlined />
                <p className={`${styles.SideLinkText}`}>Platform</p>
              </MuiLink>
              <ul className={`${styles.DropDownList}`}>
                <li><Link to={"/super-admin/dashboard"} onClick={closeAll}>Dashboard</Link></li>
                <li><Link to={"/super-admin/companies"} onClick={closeAll}>Companies</Link></li>
                <li><Link to={"/super-admin/activity"} onClick={closeAll}>Activity Log</Link></li>
              </ul>
            </li>}
            {isSuper && <li {...menuProps('billing')} className={dropClass('billing', ['sa-invoices', 'sa-revenue', 'sa-addons', 'sa-settings'])}>
              <MuiLink onClick={(e) => toggleMenu('billing', e)}>
                <ReceiptLongOutlined />
                {overdueCount > 0 && <span className={`${styles.RailCountBadge}`} title={`${overdueCount} overdue invoice(s)`}>{overdueCount > 99 ? '99+' : overdueCount}</span>}
                <p className={`${styles.SideLinkText}`}>Billing</p>
              </MuiLink>
              <ul className={`${styles.DropDownList}`}>
                <li><Link to={"/super-admin/invoices"} onClick={closeAll}>Invoices{overdueCount > 0 ? ` (${overdueCount} overdue)` : ''}</Link></li>
                <li><Link to={"/super-admin/revenue"} onClick={closeAll}>Revenue</Link></li>
                <li><Link to={"/super-admin/addons"} onClick={closeAll}>Add-ons &amp; Pricing</Link></li>
                <li><Link to={"/super-admin/settings"} onClick={closeAll}>Billing Settings</Link></li>
              </ul>
            </li>}

            {!isSuper && (isCompanyAdmin || can('dashboard:view')) && <li className={menuName === "dashboard" ? `${styles.Active}` : ``}>
              <Link to={"/dashboard"} onClick={closeAll}>
                <TuneOutlined />
                <p className={`${styles.SideLinkText}`}>Dashboard</p>
              </Link>
            </li>}
            {!isSuper && (isCompanyAdmin || can('dashboard:live-tracking')) && <li className={menuName === "livetracking" ? `${styles.Active}` : ``}>
              <Link to={"/live-tracking"} onClick={closeAll}>
                <MyLocationOutlined />
                {!liveTrackingOn && <LockBadge />}
                <p className={`${styles.SideLinkText}`}>Live Tracking</p>
              </Link>
            </li>}
            {(isSuper || isCompanyAdmin || can('bookings:list') || can('bookings:create') || can('bookings:assign-driver') || can('bookings:driver-jobs') || can('bookings:picked-items')) && <li {...menuProps('booking')} className={dropClass('booking')}>
              <MuiLink onClick={(e) => toggleMenu('booking', e)}>
                <CalendarTodayOutlined />
                <p className={`${styles.SideLinkText}`}>Bookings</p>
              </MuiLink>
              <ul className={`${styles.DropDownList}`}>
                {!isSuper && (isCompanyAdmin || can('bookings:create')) && <li><Link to={"/booking/add-booking"} onClick={closeAll}>Make A Booking</Link></li>}
                {!isSuper && shippingOn && (isCompanyAdmin || can('bookings:create')) && <li><Link to={"/booking/add-delivery-booking"} onClick={closeAll}>Make A Delivery Booking</Link></li>}
                {!isSuper && (isCompanyAdmin || can('bookings:assign-driver')) && <li><Link to={"/booking/assign-drivers"} onClick={closeAll}>Assign Bookings To Driver</Link></li>}
                {(isSuper || isCompanyAdmin || can('bookings:list')) && <li><Link to={"/booking/list"} onClick={closeAll}>View All Bookings</Link></li>}
                {!isSuper && (isCompanyAdmin || can('bookings:driver-jobs')) && <li><Link to={"/booking/driver-jobs"} onClick={closeAll}>View Driver Jobs</Link></li>}
                {!isSuper && (isCompanyAdmin || can('bookings:picked-items')) && <li><Link to={"/booking/picked-items"} onClick={closeAll}>View Picked Items</Link></li>}
              </ul>
            </li>}
            {(isSuper || isCompanyAdmin || can('shipping:container-list') || can('shipping:list') || can('shipping:add')) && <li {...menuProps('shipping')} className={dropClass('shipping')}>
              <MuiLink onClick={(e) => toggleMenu('shipping', e)}>
                <SendOutlined />
                {!shippingOn && <LockBadge />}
                <p className={`${styles.SideLinkText}`}>Shippings</p>
              </MuiLink>
              <ul className={`${styles.DropDownList}`}>
                {(isSuper || isCompanyAdmin || can('shipping:container-list')) && <li><Link to={"/shipping/container-list"} onClick={closeAll}>Containers</Link></li>}
                {!isSuper && (isCompanyAdmin || can('shipping:add')) && <li><Link to={"/shipping/add"} onClick={closeAll}>Add Shipping</Link></li>}
                {(isSuper || isCompanyAdmin || can('shipping:list')) && <li><Link to={"/shipping/list"} onClick={closeAll}>View All Shippings</Link></li>}
              </ul>
            </li>}
            {!isSuper && (isCompanyAdmin || can('delivery:receive-shipping') || can('delivery:assign-driver') || can('delivery:driver-jobs')) && <li {...menuProps('delivery')} className={dropClass('delivery')}>
              <MuiLink onClick={(e) => toggleMenu('delivery', e)}>
                <LocalShippingOutlined />
                {!shippingOn && <LockBadge />}
                <p className={`${styles.SideLinkText}`}>Delivery</p>
              </MuiLink>
              <ul className={`${styles.DropDownList}`}>
                {(isCompanyAdmin || can('delivery:receive-shipping')) && <li><Link to={"/delivery/receive-shipping"} onClick={closeAll}>Receive Shipping</Link></li>}
                {(isCompanyAdmin || can('delivery:assign-driver')) && <li><Link to={"/delivery/assign-to-driver"} onClick={closeAll}>Assign Booking For Delivery</Link></li>}
                {(isCompanyAdmin || can('delivery:driver-jobs')) && <li><Link to={"/delivery/driver-jobs"} onClick={closeAll}>View Driver Job For Delivery</Link></li>}
              </ul>
            </li>}
            {(isSuper || isCompanyAdmin || can('customers:list')) && <li className={menuName === "customer" ? `${styles.Active}` : ``}>
              <Link to={"/customer/list"} onClick={closeAll}>
                <PersonOutlineOutlined />
                <p className={`${styles.SideLinkText}`}>Customers</p>
              </Link>
            </li>}
            {(isSuper || isCompanyAdmin || can('drivers:list')) && <li className={menuName === "driver" ? `${styles.Active}` : ``}>
              <Link to={"/driver/list"} onClick={closeAll}>
                <PeopleOutlineOutlined />
                <p className={`${styles.SideLinkText}`}>Drivers</p>
              </Link>
            </li>}
            {(isSuper || isCompanyAdmin || can('items:list')) && <li {...menuProps('items')} className={dropClass('items')}>
              <MuiLink onClick={(e) => toggleMenu('items', e)}>
                <InventoryOutlined />
                <p className={`${styles.SideLinkText}`}>Items</p>
              </MuiLink>
              <ul className={`${styles.DropDownList}`}>
                {shippingOn && <li><Link to={"/delivery-product-category/list"} onClick={closeAll}>Delivery Item Categories</Link></li>}
                {shippingOn && <li><Link to={"/delivery-product/list"} onClick={closeAll}>Delivery Items</Link></li>}
                <li><Link to={"/product-category/list"} onClick={closeAll}>PIckup Item Categories</Link></li>
                <li><Link to={"/product/list"} onClick={closeAll}>PIckup Items</Link></li>
              </ul>
            </li>}
            {(isSuper || isCompanyAdmin || can('report:driver-report') || can('report:container-report') || can('report:customer-orders') || can('report:payment-report') || can('report:user-tracking-report')) && <li {...menuProps('report')} className={dropClass('report')}>
              <MuiLink onClick={(e) => toggleMenu('report', e)}>
                <AssessmentOutlined />
                <p className={`${styles.SideLinkText}`}>Reports</p>
              </MuiLink>
              <ul className={`${styles.DropDownList}`}>
                {(isSuper || isCompanyAdmin || can('report:driver-report')) && <li><Link to={"/report/driver-report"} onClick={closeAll}>Driver Report</Link></li>}
                {(isSuper || isCompanyAdmin || can('report:container-report')) && <li><Link to={"/report/container-report"} onClick={closeAll}>Container Report</Link></li>}
                {(isSuper || isCompanyAdmin || can('report:customer-orders')) && <li><Link to={"/report/customer-orders"} onClick={closeAll}>Customer Orders</Link></li>}
                {(isSuper || isCompanyAdmin || can('report:payment-report')) && <li><Link to={"/report/payment-report"} onClick={closeAll}>Payment Report</Link></li>}
                {(isSuper || isCompanyAdmin || can('report:user-tracking-report')) && <li><Link to={"/report/user-tracking-report"} onClick={closeAll}>User Tracking Report</Link></li>}
              </ul>
            </li>}
            {(isSuper || isCompanyAdmin || can('settings:membership-list') || can('settings:user-list') || can('settings:user-role-list') || can('settings:user-permission')) && <li {...menuProps('settings')} className={dropClass('settings', ['settings', 'membership', 'companyprofile'])}>
              <MuiLink onClick={(e) => toggleMenu('settings', e)}>
                <SettingsOutlined />
                <p className={`${styles.SideLinkText}`}>Settings</p>
              </MuiLink>
              <ul className={`${styles.DropDownList}`}>
                {!isSuper && (isCompanyAdmin || can('settings:membership-list')) && <li><Link to={"/membership/list"} onClick={closeAll}>Membership Management{!membershipOn ? ' 🔒' : ''}</Link></li>}
                {(isCompanyAdmin || can('settings:user-list')) && <li><Link to={"/user/list"} onClick={closeAll}>User Management</Link></li>}
                {(isSuper || isCompanyAdmin) && <li><Link to={"/email-template/list"} onClick={closeAll}>Email Templates</Link></li>}
                {(isCompanyAdmin || can('settings:user-role-list')) && <li><Link to={"/user-role/list"} onClick={closeAll}>User Roles</Link></li>}
                {(isSuper || isCompanyAdmin || can('settings:user-permission')) && <li><Link to={"/user-permissions"} onClick={closeAll}>User Permissions</Link></li>}
                {isCompanyAdmin && <li><Link to={"/company-profile?tab=addons"} onClick={closeAll}>Service Add-ons</Link></li>}
                {isCompanyAdmin && <li><Link to={"/company-profile?tab=billing"} onClick={closeAll}>Billing &amp; Invoices</Link></li>}
                {isCompanyAdmin && <li><Link to={"/company-profile"} onClick={closeAll}>Company Settings</Link></li>}
              </ul>
            </li>}
          </ul>
        </div>
      </div>
    </footer>
  </>);
};

export default Footer;
